import React, { useRef, useState } from "react";
import { FileText, Upload, Trash2, ExternalLink, Loader2, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, DOC_TYPE_LABELS } from "../lib/api";

// Slots fixos exibidos na ordem do fluxo de cadastro
const FIXED_SLOTS = [
  "documento_frente",
  "documento_verso",
  "selfie_documento",
  "comprovante_residencia",
  "comprovante_renda",
  "foto_fachada",
];

// Tipos extras que aparecem como cards adicionais (multi-upload)
const EXTRA_TYPES = ["foto_interna", "doc_adicional"];

export function DocCard({ docType, doc, clientId, canEdit, onPreview, onChanged, isExtra = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo maior que 8MB");
      return;
    }
    setBusy(true);
    try {
      // Se já existir um documento neste slot fixo, remove o antigo antes de subir o novo
      if (doc?.id && !isExtra) {
        try { await api.delete(`/clients/${clientId}/documents/${doc.id}`); } catch (_) { /* ignora */ }
      }
      const fd = new FormData();
      fd.append("document_type", docType);
      fd.append("file", file);
      await api.post(`/clients/${clientId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${DOC_TYPE_LABELS[docType]} enviado`);
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao enviar documento");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!doc?.id) return;
    if (!window.confirm(`Remover ${DOC_TYPE_LABELS[docType] || docType}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${clientId}/documents/${doc.id}`);
      toast.success("Documento removido");
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao remover");
    } finally {
      setBusy(false);
    }
  };

  const filled = !!doc?.id;
  return (
    <div
      className={`relative p-3.5 rounded-md border bg-[#0f0f0f] transition-colors ${filled ? "border-[#262626]" : "border-dashed border-[#3a3a3a]"}`}
      data-testid={`doc-slot-${docType}${isExtra && doc ? `-${doc.id}` : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center flex-shrink-0">
          <FileText className="text-[#D4AF37]" size={16}/>
        </div>
        {filled ? (
          <span className="badge-base bg-emerald-500/10 border-emerald-500/30 text-emerald-300 text-[10.5px]">
            <CheckCircle2 size={10}/> Recebido
          </span>
        ) : (
          <span className="text-[10.5px] uppercase tracking-wider text-[#737373]">Pendente</span>
        )}
      </div>
      <div className="text-[13px] font-medium mt-3">{DOC_TYPE_LABELS[docType] || docType}</div>
      {filled && (
        <div className="text-[11px] text-[#737373] truncate mt-0.5" title={doc.original_filename}>
          {doc.original_filename}
        </div>
      )}

      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {filled && (
          <button
            type="button"
            onClick={() => onPreview(doc)}
            className="fc-btn-ghost text-[11.5px] py-1.5 px-2.5 flex items-center gap-1"
            data-testid={`doc-view-${doc.id}`}
          >
            <ExternalLink size={11}/> Ver
          </button>
        )}
        {canEdit && (
          <>
            <label
              className={`fc-btn-ghost text-[11.5px] py-1.5 px-2.5 flex items-center gap-1 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
              data-testid={`doc-upload-${docType}${isExtra && doc ? `-${doc.id}` : ""}`}
            >
              {busy ? <Loader2 size={11} className="animate-spin"/> : <Upload size={11}/>}
              {filled && !isExtra ? "Substituir" : "Enviar"}
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                disabled={busy}
              />
            </label>
            {filled && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="fc-btn-ghost text-[11.5px] py-1.5 px-2.5 flex items-center gap-1 text-red-300 border-red-500/20 hover:border-red-500/40"
                data-testid={`doc-remove-${doc.id}`}
              >
                <Trash2 size={11}/> Remover
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function DocumentSlots({ clientId, documents, canEdit, onPreview, onChanged }) {
  const [addingExtraType, setAddingExtraType] = useState(null); // 'foto_interna' | 'doc_adicional' | null
  const addRef = useRef(null);

  // Indexa por tipo
  const byType = {};
  for (const d of documents) {
    byType[d.document_type] = byType[d.document_type] || [];
    byType[d.document_type].push(d);
  }

  const extraDocs = EXTRA_TYPES.flatMap((t) => (byType[t] || []).map((d) => ({ ...d, _docType: t })));

  const uploadExtra = async (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo maior que 8MB");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("document_type", docType);
      fd.append("file", file);
      await api.post(`/clients/${clientId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${DOC_TYPE_LABELS[docType]} enviado`);
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao enviar");
    } finally {
      setAddingExtraType(null);
      if (addRef.current) addRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FIXED_SLOTS.map((t) => (
          <DocCard
            key={t}
            docType={t}
            doc={(byType[t] || [])[0]}
            clientId={clientId}
            canEdit={canEdit}
            onPreview={onPreview}
            onChanged={onChanged}
          />
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[11px] uppercase tracking-wider text-[#737373]">Documentos / fotos adicionais</div>
          {canEdit && (
            <div className="flex gap-1.5">
              <label
                className="fc-btn-ghost text-[11.5px] py-1.5 px-2.5 flex items-center gap-1 cursor-pointer"
                data-testid="doc-add-foto_interna"
              >
                <Plus size={11}/> Foto interna
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => uploadExtra("foto_interna", e.target.files?.[0])}
                />
              </label>
              <label
                className="fc-btn-ghost text-[11.5px] py-1.5 px-2.5 flex items-center gap-1 cursor-pointer"
                data-testid="doc-add-doc_adicional"
              >
                <Plus size={11}/> Documento adicional
                <input
                  ref={addRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => uploadExtra("doc_adicional", e.target.files?.[0])}
                />
              </label>
            </div>
          )}
        </div>

        {extraDocs.length === 0 ? (
          <div className="text-[12.5px] text-[#737373] p-3.5 rounded-md border border-dashed border-[#262626] bg-[#0a0a0a]">
            Nenhum documento adicional enviado.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {extraDocs.map((d) => (
              <DocCard
                key={d.id}
                docType={d._docType}
                doc={d}
                clientId={clientId}
                canEdit={canEdit}
                onPreview={onPreview}
                onChanged={onChanged}
                isExtra
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
